import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { setHeading } from "../../../accounts/account.actions";
import { useTranslation } from "react-i18next";
import { GenerateOrderPDF } from "./ConstructionOrderAction";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintIcon from "@mui/icons-material/Print";
import { baseURL } from "../../../../../global/api";


const STATUS_CONFIG = {
    Completed: { color: "#059669", bg: "#d1fae5", icon: "✓" },
    Pending: { color: "#d97706", bg: "#fef3c7", icon: "◷" },
    Cancelled: { color: "#dc2626", bg: "#fee2e2", icon: "✕" },
    Processing: { color: "#6366f1", bg: "#ede9fe", icon: "⟳" },
};
function getStatusCfg(status) {
    return STATUS_CONFIG[status] ?? { color: "#6b7280", bg: "#f3f4f6", icon: "•" };
}
const fmt = (n) => Number(n ?? 0).toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    return isNaN(date) ? d : date.toLocaleDateString("en-PK", { day: "2-digit", month: "short", year: "numeric" });
};

// ─── Component ─────────────────────────────────────────────────────────────────
const DetailViewCashService = (props) => {
    const role = localStorage.getItem("role");
    const { t } = useTranslation();
    const location = useLocation();
    const fromPath = location.state?.from;
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);

    useEffect(() => {
        props.setHeading("View Service Details");
    });

    const d = props.constructionOrderWithIdData || {};
    const filteredProductExpenses = selectedCategoryId ? d.productExpenseData?.filter(p => p.categoryId?._id === selectedCategoryId) : d.productExpenseData;
    const filteredServiceExpenses = selectedCategoryId ? d.extraServiceExpenseData?.filter(e => e.categoryId?._id === selectedCategoryId) : d.extraServiceExpenseData;


    if (props.isFetchingConstructionOrderWithId) {
        return (
            <div style={s.loadWrap}>
                <div style={s.spinner} />
                <span style={{ fontSize: 14 }}>Loading service details…</span>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    const cfg = getStatusCfg(d.status);
    const productCols = ["Category", "Product Name", "Qty", "Unit Price", "Total"];
    const serviceCols = ["Category", "Date", "Invoice No", "Supplier", "VATIN", "Amount", "VAT %", "Total", "Description", "File"];

    const productTotal = (filteredProductExpenses || []).reduce(
        (sum, item) => sum + Number(item.totalSalePrice || 0),
        0
    );
    const serviceTotal = (filteredServiceExpenses || []).reduce(
        (sum, item) => sum + Number(item.total || 0),
        0
    );

    const expensesTotal = productTotal + serviceTotal;

    return (
        <div style={s.page}>
            <div style={s.main}>
                {/* ── Top Bar ── */}
                <div style={s.topBar}>
                    <div>
                        <h1 style={s.pageTitle}>{t("ConstructionOrder")}</h1>
                        {/* <p style={s.pageSubtitle}>Order #{d.sequenceNumber}</p> */}
                    </div>
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <Link to={fromPath} style={s.backBtn}><ArrowBackIcon style={{ fontSize: 16 }} />Back to List</Link>
                        <button
                            onClick={() => props.GenerateOrderPDF(d)}
                            className="no-print"
                            style={s.printBtn}
                        >
                            <PrintIcon style={{ fontSize: 17 }} />
                            Print
                        </button>
                    </div>
                </div>

                {/* ── Header Card ── */}
                <div style={s.detailHeader}>
                    <div style={{ flex: 1 }}>
                        <p style={s.detailMeta}>{t("ConstructionOrder")}</p>
                        <h2 style={s.detailTitle}>#{d.sequenceNumber ?? "—"}
                            <span style={{ ...s.badge, color: cfg.color, background: cfg.bg }}>
                                {cfg.icon} {d.status}
                            </span>
                        </h2>
                        <p style={s.detailSub}> {t("Customer")} :&nbsp;<strong>{d.customerDetails?.name ?? "—"}</strong></p>
                    </div>
                    <div style={s.totalBox}>
                        <p style={s.totalBoxLabel}>{t("Order Profit")}</p>
                        <p style={s.totalBoxAmount}>{role === "admin" ? fmt(d.finalTotal) : "*****"}</p>
                        {/* <p style={{ ...s.totalBoxLabel, marginTop: 10 }}>Balance Due</p> */}
                        {/* <p style={{ ...s.totalBoxAmount, fontSize: 17, color: remaining > 0 ? "#dc2626" : "#059669" }}> {fmt(remaining)}</p> */}
                    </div>
                </div>

                {/* ── Info Grid ── */}
                <div style={s.infoGrid}>
                    {[
                        [t("Date"), fmtDate(d.date)],
                        [t("DeliveryDate"), fmtDate(d.deliveryDate)],
                        [t("SellingPrice"), " " + (role === "admin" ? fmt(d.sellingPrice) : "*****")],
                        [t("Expense"), " " + (role === "admin" ? fmt(d.totalExpense) : "*****")],
                        // ["VAT (5%)", "PKR " + fmt(d.vatTotal)],
                        // ["Advance Paid", "PKR " + fmt(d.advance)],
                    ].map(([label, val]) => (
                        <div key={label} style={s.infoField}>
                            <p style={s.fieldLabel}>{label}</p>
                            <p style={s.fieldValue}>{val}</p>
                        </div>
                    ))}
                </div>

                {/* ── Categories ── */}
                {d.categories?.length > 0 && (
                    <div style={s.section}>
                        <p style={s.sectionTitle}> <span style={s.sectionTitleBar} />{t("Category")}</p>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            <span
                                onClick={() => setSelectedCategoryId(null)}
                                style={{
                                    ...s.catChip,
                                    cursor: "pointer",
                                    background: selectedCategoryId === null ? "#3b82f6" : "#eff6ff",
                                    color: selectedCategoryId === null ? "#fff" : "#3b82f6",
                                }}
                            >
                                All
                            </span>
                            {d.categories.map((cat, i) => (
                                <span
                                    key={i}
                                    onClick={() => setSelectedCategoryId(prev => prev === cat._id ? null : cat._id)}
                                    style={{
                                        ...s.catChip,
                                        cursor: "pointer",
                                        background: selectedCategoryId === cat._id ? "#3b82f6" : "#eff6ff",
                                        color: selectedCategoryId === cat._id ? "#fff" : "#3b82f6",
                                    }}
                                >
                                    {cat.name}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* ── Product Expenses ── */}
                <div style={s.section}>
                    <p style={s.sectionTitle}><span style={s.sectionTitleBar} />{t("Package Expenses")}</p>
                    <div style={s.tableWrap}>
                        <table style={s.table}>
                            <thead>
                                <tr>
                                    <th style={{ ...s.th, width: 40, textAlign: "center" }}>#</th>
                                    {productCols.map(h => <th key={h} style={s.th}>{h}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {(filteredProductExpenses || []).length > 0 ? (
                                    (filteredProductExpenses || []).map((p, i) => (
                                        <tr key={i} style={{ background: i % 2 ? "#f8fafc" : "#fff" }}>
                                            <td style={{ ...s.td, textAlign: "center", color: "#9ca3af", fontSize: 12 }}>{i + 1}</td>
                                            <td style={s.td}>{p.categoryId.name}</td>
                                            <td style={s.td}>{p.productName}</td>
                                            <td style={s.td}>{p.quantity}</td>
                                            <td style={s.td}>{role === "admin" ? fmt(p.salePrice) : "****"}</td>
                                            <td style={s.tdHighlight}>{role === "admin" ? fmt(p.totalSalePrice) : "****"}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={5} style={{ ...s.td, textAlign: "center", color: "#9ca3af", fontStyle: "italic", padding: "28px 14px" }}>
                                            No package expenses recorded
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            {(filteredProductExpenses || []).length > 0 && (
                                <tr style={{ background: "#eef2ff" }}>
                                    <td colSpan={5} style={{ ...s.td, fontWeight: 700, textAlign: "right" }}>
                                        Total
                                    </td>
                                    <td style={{ ...s.tdHighlight }}>
                                        {role === "admin" ? fmt(productTotal) : "****"}
                                    </td>
                                </tr>
                            )}
                        </table>
                    </div>
                </div>

                {/* ── Extra Service Expenses ── */}
                <div style={s.section}>
                    <p style={s.sectionTitle}><span style={s.sectionTitleBar} />{t("Extra Service Expenses")}</p>
                    <div style={s.tableWrap}>
                        <table style={s.table}>
                            <thead>
                                <tr>
                                    <th style={{ ...s.th, width: 40, textAlign: "center" }}>#</th>
                                    {serviceCols.map(h => <th key={h} style={s.th}>{h}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {(filteredServiceExpenses || []).length > 0 ? (
                                    (filteredServiceExpenses || []).map((e, i) => (
                                        <tr key={i} style={{ background: i % 2 ? "#f8fafc" : "#fff" }}>
                                            <td style={{ ...s.td, textAlign: "center", color: "#9ca3af", fontSize: 12 }}> {i + 1}</td>
                                            <td style={s.td}>{e.categoryId?.name || "—"} </td>
                                            <td style={s.td}>{fmtDate(e.date)}</td>
                                            <td style={s.td}>{e.invoiceNo || "—"}</td>
                                            <td style={s.td}>{e.supplierName || "—"}</td>
                                            <td style={s.td}> {e.vatinNo || "—"}</td>
                                            <td style={s.td}>{role === "admin" ? fmt(e.amount) : "****"}</td>
                                            <td style={s.td}>{e.vat ?? 0}%</td>
                                            <td style={s.tdHighlight}>{role === "admin" ? fmt(e.total) : "****"}</td>
                                            <td style={s.td}>{e.description || "—"}</td>
                                            <td style={s.td}>
                                                {e.uploader ? (
                                                    <a
                                                        href={`${baseURL}/assets/${e.uploader}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        style={{
                                                            color: "#2563eb",
                                                            fontWeight: 600,
                                                            textDecoration: "none",
                                                        }}
                                                    >
                                                        View File
                                                    </a>
                                                ) : (
                                                    "—"
                                                )}
                                            </td>

                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={11}
                                            style={{
                                                ...s.td,
                                                textAlign: "center",
                                                color: "#9ca3af",
                                                fontStyle: "italic",
                                                padding: "28px 14px"
                                            }}
                                        >
                                            No extra service expenses recorded
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            {(filteredServiceExpenses || []).length > 0 && (
                                <tr style={{ background: "#eef2ff" }}>
                                    <td colSpan={8} style={{ ...s.td, fontWeight: 700, textAlign: "right" }}> Total</td>
                                    <td style={{ ...s.tdHighlight }}>
                                        {role === "admin" ? fmt(serviceTotal) : "****"}
                                    </td>
                                    <td colSpan={2}></td>
                                </tr>
                            )}
                        </table>
                    </div>
                </div>

                {/* ── Financial Summary ── */}
                <div style={s.summaryBox}>
                    <p style={{ ...s.sectionTitle, marginBottom: 12 }}><span style={s.sectionTitleBar} />Financial Summary</p>
                    {[
                        [t("SellingPrice"), role === "admin" ? fmt(d.sellingPrice) : "*****", false, false],
                        [t("Package Expenses"), role === "admin" ? fmt(productTotal) : "*****", false, false],
                        [t("Extra Service Expenses"), role === "admin" ? fmt(serviceTotal) : "*****", false, false],
                        // [t("VAT"), fmt(d.vatTotal), false, false],
                        [t("FinalTotal"), role === "admin" ? fmt(d.sellingPrice - expensesTotal) : "*****", true, true],
                    ].map(([label, val, bold, divider], i) => (
                        <React.Fragment key={label}>
                            {divider && <hr style={{ border: "none", borderTop: "2px solid #e5e7eb", margin: "6px 0" }} />}
                            <div style={{
                                ...s.summaryRow,
                                fontWeight: bold ? 700 : 500,
                                fontSize: bold ? 16 : 14,
                            }}>
                                <span>{label}</span>
                                <span style={{ fontFamily: "'DM Mono', monospace", color: bold ? "#3a6fa8" : "inherit" }}>
                                    {val}
                                </span>
                            </div>
                        </React.Fragment>
                    ))}
                </div>

                <style>{`@keyframes spin { to { transform: rotate(360deg); } } @media print { .no-print { display: none !important; } }`}</style>
            </div>
        </div>
    );
};

const s = {
    page: { minHeight: "100vh", background: "#f0f2f7", fontFamily: "'Segoe UI', system-ui, sans-serif" },
    main: { maxWidth: 'auto', margin: "0 auto", padding: "28px 24px" },
    topBar: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 },
    backBtn: { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 18px", borderRadius: 8, border: "1.5px solid #d1d5db", background: "#fff", color: "#374151", fontWeight: 600, fontSize: 14, cursor: "pointer", textDecoration: "none", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", transition: "box-shadow .2s", },
    printBtn: { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 18px", borderRadius: 8, border: "none", background: "#3a6fa8", color: "#fff", fontWeight: 600, fontSize: 14, cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,0.1)", },
    pageTitle: { fontSize: 22, fontWeight: 800, color: "#1e293b", margin: "0 0 4px" },
    pageSubtitle: { fontSize: 14, color: "#9ca3af", margin: 0 },
    detailHeader: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", background: "#fff", borderRadius: 14, padding: "24px 28px", marginBottom: 16, boxShadow: "0 2px 10px rgba(0,0,0,0.07)", flexWrap: "wrap", gap: 16, },
    detailMeta: { margin: 0, fontSize: 11, color: "#9ca3af", letterSpacing: 1, textTransform: "uppercase" },
    detailTitle: { margin: "4px 0 0", fontSize: 26, fontWeight: 800, color: "#1e293b", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10 },
    detailSub: { margin: "8px 0 0", fontSize: 14, color: "#6b7280" },
    badge: { display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700, letterSpacing: 0.4 },
    totalBox: { textAlign: "right", background: "#f8fafc", borderRadius: 10, padding: "16px 22px", minWidth: 210, border: "1px solid #e5e7eb" },
    totalBoxLabel: { margin: 0, fontSize: 11, color: "#9ca3af", letterSpacing: 1, textTransform: "uppercase" },
    totalBoxAmount: { margin: "4px 0 0", fontSize: 22, fontWeight: 800, color: "#1e293b" },
    infoGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 12, marginBottom: 16 },
    infoField: { background: "#fff", borderRadius: 10, padding: "14px 18px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" },
    fieldLabel: { margin: 0, fontSize: 10, color: "#9ca3af", letterSpacing: 1, textTransform: "uppercase", fontWeight: 600 },
    fieldValue: { margin: "5px 0 0", fontSize: 15, fontWeight: 700, color: "#1e293b" },
    section: { background: "#fff", borderRadius: 12, padding: "18px 20px", marginBottom: 14, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" },
    sectionTitle: { margin: "0 0 12px", fontSize: 13, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: 0.5 },
    sectionTitleBar: { width: 3, height: 14, borderRadius: 2, background: "#3a6fa8", flexShrink: 0 },
    catChip: { padding: "4px 12px", borderRadius: 20, background: "#eff6ff", color: "#3b82f6", fontSize: 13, fontWeight: 500 },
    tableWrap: { overflowX: "auto" },
    table: { width: "100%", borderCollapse: "collapse" },
    th: { textAlign: "left", padding: "10px 14px", fontSize: 11, color: "#6b7280", fontWeight: 700, background: "#f8fafc", letterSpacing: 0.6, textTransform: "uppercase", borderBottom: "2px solid #e5e7eb" },
    td: { padding: "11px 14px", fontSize: 14, color: "#374151", borderBottom: "1px solid #f1f5f9" },
    tdHighlight: { padding: "11px 14px", fontSize: 14, fontWeight: 700, color: "#3a6fa8", borderBottom: "1px solid #f1f5f9" },
    summaryBox: { background: "#fff", borderRadius: 12, padding: "20px 24px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", maxWidth: 400, marginLeft: "auto" },
    summaryRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" },
    loadWrap: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", gap: 16, fontFamily: "'Segoe UI', system-ui, sans-serif", color: "#6b7280" },
    spinner: { width: 38, height: 38, border: "3px solid #e5e7eb", borderTop: "3px solid #5b8fc9", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
};

const mapDispatchToProps = (dispatch) => ({
    setHeading: (data) => dispatch(setHeading(data)),
    GenerateOrderPDF: (orderData) => dispatch(GenerateOrderPDF(orderData)),
});

const mapStateToProps = (state) => ({
    isFetchingConstructionOrderWithId: state.ConstructionOrderReducer.isFetchingConstructionOrderWithId,
    constructionOrderWithIdData: state.ConstructionOrderReducer.constructionOrderWithIdData,
});

export default connect(mapStateToProps, mapDispatchToProps)(DetailViewCashService);